/**
 * Navigation Slice
 *
 * `registryByScope` holds effective menu trees from GET /api/navigation/registry per scope.
 * Each fetch merges only the requested scope(s) so admin refreshes (e.g. branch feature CRUD on
 * platform shell) do not clear the active context menu.
 *
 * Sidebar / search / breadcrumbs consume the same policies via selectors (`selectSidebarMenu`,
 * `selectEffectiveNavigationPolicies`).
 */

import { createSlice, PayloadAction, createAsyncThunk } from '@reduxjs/toolkit'
import { MenuItem } from '@/types/shared/common.types'
import type { MenuPolicy } from '@/config/navigation/menu.config.generated.types'
import { logout } from '@/store/slices/auth.slice'
import { navigationService, type NavigationRouteCatalogEntry } from '@/services/api/shared/navigation.service'
import { registryToMenuPolicies, type Scope } from '@/utils/navigation/registryToMenuPolicies'

export const SCOPES: Scope[] = ['platform', 'organization', 'branch']

/** Fetch scoped registry via GET /api/navigation/registry (context JWT — server-filtered PCS). Throws on failure so bootstrap can hard-fail. */
export const fetchNavigationRegistry = createAsyncThunk(
  'navigation/fetchRegistry',
  async (
    scopeOrAll?: Scope
  ): Promise<{
    registries: Partial<Record<Scope, MenuPolicy[]>>
    routeCatalogs: Partial<Record<Scope, NavigationRouteCatalogEntry[]>>
  }> => {
    const scopes: Scope[] = scopeOrAll ? [scopeOrAll] : SCOPES
    const registries: Partial<Record<Scope, MenuPolicy[]>> = {}
    const routeCatalogs: Partial<Record<Scope, NavigationRouteCatalogEntry[]>> = {}
    await Promise.all(
      scopes.map(async (scope) => {
        const [raw, catalog] = await Promise.all([
          navigationService.getRegistry(scope),
          navigationService.getRouteCatalog(scope),
        ])
        registries[scope] = registryToMenuPolicies(raw, scope)
        routeCatalogs[scope] = catalog
      })
    )
    return { registries, routeCatalogs }
  }
)

interface NavigationState {
  navigation: any | null
  sidebarMenu: MenuItem[] | null
  homeRedirectUrl: string | null
  firstNavigationUrl: string | null
  isLoading: boolean
  error: string | null
  /** Runtime PCS-filtered registry from GET /navigation/registry — required for sidebar; no static-codegen fallback at runtime. */
  registryByScope: Partial<Record<Scope, MenuPolicy[]>>
  /** All menu routes for scope (incl. disabled) from GET /navigation/route-catalog — direct URL guard. */
  routeCatalogByScope: Partial<Record<Scope, NavigationRouteCatalogEntry[]>>
}

const initialState: NavigationState = {
  navigation: null,
  sidebarMenu: null,
  homeRedirectUrl: null,
  firstNavigationUrl: null,
  isLoading: false,
  error: null,
  registryByScope: {},
  routeCatalogByScope: {},
}

const navigationSlice = createSlice({
  name: 'navigation',
  initialState,
  reducers: {
    setNavigation: (state, action: PayloadAction<any>) => {
      state.navigation = action.payload
    },
    setSidebarMenu: (state, action: PayloadAction<MenuItem[] | null>) => {
      state.sidebarMenu = action.payload
    },
    setHomeRedirectUrl: (state, action: PayloadAction<string | null>) => {
      state.homeRedirectUrl = action.payload
    },
    setFirstNavigationUrl: (state, action: PayloadAction<string | null>) => {
      state.firstNavigationUrl = action.payload
    },
    setLoading: (state, action: PayloadAction<boolean>) => {
      state.isLoading = action.payload
    },
    setError: (state, action: PayloadAction<string | null>) => {
      state.error = action.payload
    },
    setRegistryByScope: (state, action: PayloadAction<Partial<Record<Scope, MenuPolicy[]>>>) => {
      state.registryByScope = { ...(action.payload || {}) }
    },
    setRouteCatalogByScope: (
      state,
      action: PayloadAction<Partial<Record<Scope, NavigationRouteCatalogEntry[]>>>
    ) => {
      state.routeCatalogByScope = { ...(action.payload || {}) }
    },
    resetNavigation: (state) => {
      state.navigation = null
      state.sidebarMenu = null
      state.homeRedirectUrl = null
      state.firstNavigationUrl = null
      state.error = null
      state.registryByScope = {}
      state.routeCatalogByScope = {}
    },
  },
  extraReducers: (builder) => {
    builder.addCase(logout, () => initialState)
    builder.addCase(fetchNavigationRegistry.fulfilled, (state, action) => {
      const next = action.payload?.registries ?? {}
      const catalogs = action.payload?.routeCatalogs ?? {}
      // Merge per scope so post-mutation refresh (e.g. branch feature on platform shell) does not wipe the active context menu.
      state.registryByScope = { ...state.registryByScope, ...next } as typeof state.registryByScope
      state.routeCatalogByScope = { ...state.routeCatalogByScope, ...catalogs } as typeof state.routeCatalogByScope
    })
  },
})

export const {
  setNavigation,
  setSidebarMenu,
  setHomeRedirectUrl,
  setFirstNavigationUrl,
  setLoading,
  setError,
  setRegistryByScope,
  setRouteCatalogByScope,
  resetNavigation,
} = navigationSlice.actions

export default navigationSlice.reducer

